import { store } from '@reduxStore';
import { triniumApi } from '../../slice';
import { consignmentSlice } from './slice';
import { Leg } from '../Leg';
import { queueUncontrolledToast } from '@toast';
import { getApiErrorMessage } from '@api/getApiError';
import { Consignment, MutableConsignment } from './types';
import { isObjectNullOrEmpty } from '@/Infrastructure/Utilities/object.type';
import { getTerminalNumber } from '@hooks/useCurrentTerminal';
import type { Voyage } from '../Vessels/types';

/**
 * After a Voyage is edited, propagate its refreshed details into any cached Consignment whose job references
 * that voyage, so the Consignment Details header reflects the change without a refetch. The voyage mutation
 * doesn't carry a consignment id, so we scan the cached fetchConsignment entries and match by voyage id.
 */
export function updateConsignmentVoyageCache(mutatedVoyage: Voyage) {
  const queries = store.getState()[triniumApi.reducerPath].queries;

  Object.values(queries).forEach((entry) => {
    if (entry?.endpointName !== 'fetchConsignment') return;

    const cachedConsignment = entry.data as Consignment | undefined;
    // Compare as strings to be resilient to number/string id representations from different endpoints
    if (String(cachedConsignment?.job.voyage?.id) !== String(mutatedVoyage.id)) return;

    store.dispatch(
      consignmentSlice.util.updateQueryData(
        'fetchConsignment',
        entry.originalArgs as unknown as { terminalNumber: string; consignmentId: number },
        (draft) => {
          if (!draft.job.voyage) return;

          draft.job.voyage.voyageIn = mutatedVoyage.voyageIn;
          draft.job.voyage.voyageOut = mutatedVoyage.voyageOut;
          draft.job.voyage.etaParsedDate = mutatedVoyage.etaParsedDate;
          draft.job.voyage.cutoffParsedDate = mutatedVoyage.cutoffParsedDate;
          draft.job.voyage.firstReceivedDate = mutatedVoyage.firstReceivedDate;
        }
      )
    );
  });
}

/**
 * Action to manually execute a request for fetching the Active Leg and executing a callback when received successfully.
 */
export function fetchActiveLegByConsignment(consignmentId: number, successCallback: (activeLeg: Leg) => void) {
  /** Get the terminal number from redux  */
  const terminalNumber = store.getState().applicationState.selectedTerminal?.number;
  if (!terminalNumber) {
    throw new Error('Terminal Number is required.');
  }

  store
    .dispatch(
      consignmentSlice.endpoints.fetchConsignmentActiveLeg.initiate({
        terminalNumber: terminalNumber,
        consignmentId: consignmentId
      })
    )
    .unwrap()
    .then((result) => {
      successCallback(result);
    })
    .catch((error: unknown) => {
      store.dispatch(
        queueUncontrolledToast({
          type: 'message',
          message: getApiErrorMessage(error),
          severity: 'error'
        })
      );
    });
}

export async function sendConsignmentMutationsAsync(
  /** Identifier for the Consignment record */
  id: number,
  /** Payload for the request changes */
  mutations: Omit<MutableConsignment, 'id'>,
  /** If true, skip the RTK Query cache update in onQueryStarted */
  skipCacheUpdate?: boolean
): Promise<Consignment | null> {
  /** If we were not given any changes, return */
  if (isObjectNullOrEmpty(mutations)) {
    return null;
  }

  const terminalNumber = getTerminalNumber();

  return await store
    .dispatch(
      consignmentSlice.endpoints.patchConsignment.initiate({
        terminalNumber: terminalNumber,
        consignment: {
          id,
          ...mutations
        },
        skipCacheUpdate
      })
    )
    .unwrap();
}
